import { useState } from 'react';
import { CalendarDateInput } from '../../components/ui/CalendarDateInput';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Loader2, ArrowRight } from 'lucide-react';
import { sitesService } from '../../services/sitesService';
import {
  dateOnlyInputValue,
  formatDateOnlyNumeric,
  parseDateOnly,
} from '../../utils/date';
import { track } from '../../services/telemetry';
import type { EstCompletionPrompt } from './estCompletionPrompt';

/**
 * Past-due estimated-completion gate (Jim 8/9): "Make the inspector update
 * if it reaches expired date similar to how we make them update the BMP
 * Maps." When a site's Est. Completion has lapsed, the inspector sets a new
 * date before the flow continues. If the permit has also expired, its new
 * expiration can be entered in the same stop (optional — renewals are often
 * client-side paperwork the inspector doesn't have yet).
 */
export function EstCompletionGate({
  siteId,
  siteName,
  estimatedEndDate,
  permitExpirationDate,
  onContinue,
  mode = 'past',
}: {
  siteId: string;
  siteName: string;
  estimatedEndDate: string | null;
  permitExpirationDate?: string | null;
  onContinue: () => void;
  /** Jim 10/13: also asked when there is no date, or nobody confirmed it in 90 days. */
  mode?: Exclude<EstCompletionPrompt, null>;
}) {
  const queryClient = useQueryClient();
  const [newDate, setNewDate] = useState('');
  const [newPermitDate, setNewPermitDate] = useState('');

  const permitExpired =
    !!permitExpirationDate &&
    (parseDateOnly(permitExpirationDate) as Date) < new Date();

  const confirmMutation = useMutation({
    mutationFn: () =>
      sitesService.updateSiteDetails(siteId, {
        confirm_estimated_end_date: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-details', siteId] });
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      track('est_completion_gate_confirmed', { siteId });
      onContinue();
    },
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      sitesService.updateSiteDetails(siteId, {
        estimated_end_date: newDate,
        ...(permitExpired && newPermitDate
          ? { permit_expiration_date: newPermitDate }
          : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-details', siteId] });
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      track('est_completion_gate_updated', { siteId });
      onContinue();
    },
  });

  // A date that isn't after today would trip this gate again on the very
  // next visit (Shane's Agave, 2026-09-15: "today" was saved, so it kept
  // asking). The picker's `min` isn't honored everywhere, so enforce it here.
  const today = dateOnlyInputValue(new Date());
  const tomorrow = dateOnlyInputValue(
    new Date(Date.now() + 24 * 60 * 60 * 1000)
  );
  const dateIsFuture = !!newDate && newDate > today;

  return (
    <div className="max-w-md mx-auto space-y-4 pb-8">
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-4 flex items-start gap-3">
        <CalendarClock className="w-6 h-6 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div>
          <h2 className="font-semibold text-slate-900 dark:text-white">
            {mode === 'past'
              ? 'Estimated completion has passed'
              : mode === 'missing'
                ? 'When will this site be finished?'
                : 'Is the estimated completion still right?'}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            {mode === 'past' && estimatedEndDate ? (
              <>
                {siteName} was estimated to finish{' '}
                <span className="font-medium">
                  {formatDateOnlyNumeric(estimatedEndDate)}
                </span>
                . Set the new expected completion date to continue this
                inspection.
              </>
            ) : mode === 'missing' ? (
              <>
                {siteName} has no estimated completion date. Your best guess is
                fine; you will be asked to check it again in a few months.
              </>
            ) : (
              <>
                {siteName} is expected to finish{' '}
                <span className="font-medium">
                  {estimatedEndDate
                    ? formatDateOnlyNumeric(estimatedEndDate)
                    : '—'}
                </span>
                . Confirm it or set a new date.
              </>
            )}
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {mode === 'missing'
              ? 'Estimated completion'
              : 'New estimated completion'}
          </label>
          <CalendarDateInput
            value={newDate}
            min={tomorrow}
            onChange={setNewDate}
            aria-label="New estimated completion date"
          />
          {newDate && !dateIsFuture && (
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
              Choose a date after today — otherwise this will ask again on the
              next inspection.
            </p>
          )}
        </div>

        {permitExpired && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Permit expired {formatDateOnlyNumeric(permitExpirationDate)} — new
              expiration (if known)
            </label>
            <CalendarDateInput
              value={newPermitDate}
              min={tomorrow}
              onChange={setNewPermitDate}
              aria-label="New permit expiration date"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Leave blank if the renewal isn't in hand yet.
            </p>
          </div>
        )}

        {mode === 'stale' && (
          <button
            type="button"
            disabled={confirmMutation.isPending || saveMutation.isPending}
            onClick={() => confirmMutation.mutate()}
            className="w-full px-4 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
          >
            {confirmMutation.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : null}
            Still right
            {estimatedEndDate
              ? ` (${formatDateOnlyNumeric(estimatedEndDate)})`
              : ''}
          </button>
        )}

        {(saveMutation.isError || confirmMutation.isError) && (
          <p className="text-sm text-red-600 dark:text-red-400">
            Couldn't save the date — check your connection and try again.
          </p>
        )}

        <button
          type="button"
          disabled={!dateIsFuture || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
          className="w-full px-4 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
        >
          {saveMutation.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <ArrowRight className="w-5 h-5" />
          )}
          Save &amp; continue
        </button>
      </div>
    </div>
  );
}
